import {HttpService} from "src/modules/common/http/service/HttpService";
import {AdminBrand, BrandRequest} from "../model/BrandTypes";
import {BrandTheme} from "src/modules/brand/BrandConfig";

export class BrandAdminService {
    private static readonly BRANDS_URL = "/internal/api/brands";

    constructor(private readonly httpService: HttpService) {}

    list = (): Promise<AdminBrand[]> => {
        return this.httpService.fetchAs<AdminBrand[]>({url: BrandAdminService.BRANDS_URL});
    };

    get = (id: number): Promise<AdminBrand> => {
        return this.httpService.fetchAs<AdminBrand>({url: `${BrandAdminService.BRANDS_URL}/${id}`});
    };

    create = (body: BrandRequest): Promise<AdminBrand> =>
        this.httpService.fetchAs<AdminBrand>({
            url: BrandAdminService.BRANDS_URL, method: "POST", data: body
        });

    update = (id: number, body: BrandRequest): Promise<AdminBrand> =>
        this.httpService.fetchAs<AdminBrand>({
            url: `${BrandAdminService.BRANDS_URL}/${id}`, method: "PUT", data: body
        });

    makeDefault = (id: number): Promise<AdminBrand> =>
        this.httpService.fetchAs<AdminBrand>({
            url: `${BrandAdminService.BRANDS_URL}/${id}/default`, method: "POST"
        });

    setActive = (id: number, active: boolean): Promise<AdminBrand> =>
        this.httpService.fetchAs<AdminBrand>({
            url: `${BrandAdminService.BRANDS_URL}/${id}/active?active=${active}`, method: "POST"
        });

    slugAvailable = (slug: string, excludeId?: number): Promise<{ available: boolean }> => {
        const query = new URLSearchParams();
        query.set("slug", slug);
        if (excludeId) query.set("excludeId", String(excludeId));
        return this.httpService.fetchAs<{ available: boolean }>({
            url: `${BrandAdminService.BRANDS_URL}/slug-available?${query.toString()}`
        });
    };

    /**
     * Derives a theme for a color without saving. Lets the form show the contrast verdict and
     * derived swatches while the admin types, using the same server-side rules as the real page.
     */
    previewTheme = (color: string, overrides: Partial<BrandRequest> = {}): Promise<BrandTheme> => {
        const query = new URLSearchParams();
        query.set("color", color);
        // Only send overrides that are actually set; blank means "use the derived value".
        (["surfaceColor", "onSurfaceColor", "linkColor", "tintColor"] as const).forEach(key => {
            const value = overrides[key];
            if (value) query.set(key, value);
        });
        return this.httpService.fetchAs<BrandTheme>({
            url: `${BrandAdminService.BRANDS_URL}/preview-theme?${query.toString()}`
        });
    };

    logoUploadEnabled = (): Promise<{ enabled: boolean }> => {
        return this.httpService.fetchAs<{ enabled: boolean }>({
            url: `${BrandAdminService.BRANDS_URL}/logo-upload-enabled`
        });
    };

    /**
     * Multipart, so this bypasses HttpService — that helper JSON-encodes every body and sets a
     * JSON content type, which would corrupt the upload. Credentials are included to carry the
     * admin session cookie, matching what HttpService does.
     */
    uploadLogo = (id: number, file: File): Promise<AdminBrand> => {
        const form = new FormData();
        form.append("file", file);
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return fetch(`${base}${BrandAdminService.BRANDS_URL}/${id}/logo`, {
            method: "POST",
            body: form,
            credentials: "include"
        }).then(async response => {
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.message || "Logo upload failed");
            return body as AdminBrand;
        });
    };
}
